import { useCallback } from 'react'
import { t } from '@lingui/macro'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'

import { defaultLpsInfos, INS_OID } from '@/constants/dmp-connect'
import { InsIdentityResultCode, InsOperation } from '@/core/models/ins-audit.model'
import { useLazyCheckInsIdentityQuery } from '@/core/services/dmp-connect-js2'
import { useLogInsCall } from '@/pages/patient/model/useLogInsCall'
import { selectDMPConnectTlsiConnector } from '@/redux/reducers/dmp-connect.reducer'
import { useAppSelector } from '@/redux/rootReducer'
import { patientGenderToInsSex } from '@/utils/patient'
import { splitInsNir } from '@/shared/lib/ins-search'

import { PatientProfileFormData } from './usePatientProfileForm'

export type InsVerificationResult = 'OK' | 'KO' | 'ERROR'
export type InsVerificationPatientData = Pick<
  PatientProfileFormData,
  'insNIR' | 'insOID' | 'birthSurname' | 'surname' | 'birthFirstName' | 'gender' | 'birthday' | 'birthPlaceCode'
>

type RunInsVerificationContext = {
  patientId?: string
  patientFullName?: string
}

export function useRunInsVerification() {
  const [checkInsIdentity, { isFetching }] = useLazyCheckInsIdentityQuery()
  const { billingNumber, instanceUniqueId: tlsiConnectorId } = useAppSelector(selectDMPConnectTlsiConnector) ?? {}
  const { enqueueSnackbar } = useSnackbar()
  const logInsCall = useLogInsCall()

  const runInsVerification = useCallback(
    async (data: InsVerificationPatientData, context: RunInsVerificationContext): Promise<InsVerificationResult> => {
      enqueueSnackbar(t`INSi verification request sent`, { variant: 'info', autoHideDuration: 3000 })

      const isTemporaryIns = data.insOID === INS_OID['INS-NIA']
      const birthSurname = (data.birthSurname ?? data.surname ?? '').trim().toUpperCase()
      const birthFirstName = (data.birthFirstName ?? '').trim().toUpperCase()
      const { value: insValue, key: insKey } = splitInsNir(data.insNIR)

      const checkInsIdentityParams = {
        LpsInfos: { ...defaultLpsInfos, s_instance: tlsiConnectorId ?? '', s_billingNumber: billingNumber },
        i_sex: patientGenderToInsSex(data.gender || 'I'),
        s_birthDate: dayjs(data.birthday).format('YYYY-MM-DD'),
        s_birthName: birthSurname,
        s_birthPlace: data.birthPlaceCode,
        s_given: birthFirstName,
        s_ins: insValue,
        s_key: insKey,
        s_oid: isTemporaryIns ? INS_OID['INS-NIA'] : INS_OID['INS-NIR_prod'],
      }

      try {
        const { data: result } = await checkInsIdentity(checkInsIdentityParams)
        const isInsIdentityValid = result?.s_status === 'OK'
        const resultCode = isInsIdentityValid ? InsIdentityResultCode.UNIQUE_IDENTITY_FOUND : InsIdentityResultCode.NO_IDENTITY_FOUND

        await logInsCall({
          patient: context.patientId,
          patientFullName: context.patientFullName,
          operation: InsOperation.CHECK_INS_IDENTITY,
          resultCode,
          returnStatus: isInsIdentityValid ? 'OK' : 'KO',
          returnedIdentity: {
            insNIR: data.insNIR,
            insOID: checkInsIdentityParams.s_oid,
            birthName: birthSurname || undefined,
            birthGiven: birthFirstName || undefined,
            birthDate: checkInsIdentityParams.s_birthDate,
            sex: String(checkInsIdentityParams.i_sex),
            birthPlaceCode: data.birthPlaceCode,
          },
        })

        return isInsIdentityValid ? 'OK' : 'KO'
      } catch {
        await logInsCall({
          patient: context.patientId,
          patientFullName: context.patientFullName,
          operation: InsOperation.CHECK_INS_IDENTITY,
        })
        return 'ERROR'
      }
    },
    [billingNumber, checkInsIdentity, enqueueSnackbar, logInsCall, tlsiConnectorId]
  )

  return {
    canRunInsVerification: Boolean(tlsiConnectorId),
    isRunningInsVerification: isFetching,
    runInsVerification,
  }
}
